// Recipe Module traceability upgrade (docs/RECIPE_MODULE.md) — optional, non-blocking "which real
// lot(s) of this ingredient went into this batch" recorder, shown per ingredient line on a recipe's
// Run page. Supports splitting one ingredient across multiple lots in a single run (e.g. 10kg from
// lot A + 40kg from lot B of the same mushroom ingredient). Lot codes are sourced from the same
// real Ingredient Receiving data the rest of the app already uses, via a custom autocomplete
// dropdown (not the native `<input list> + <datalist>` — Safari, desktop and iOS, never renders
// any UI for `<datalist>` at all, silently making that "autocomplete" a dead feature for Safari
// users, which is most of this app's real floor/mobile usage) — free text is still always
// accepted, since an operator sometimes needs to type a lot the picker hasn't caught up with yet.
import React, { useState } from 'react';
import ingredientInventoryService from '../services/ingredientInventoryService';
import AutocompleteInput from './AutocompleteInput';

const LB_TO_KG = 0.453592;
const toKg = (qty, unit) => {
  const n = parseFloat(qty);
  if (isNaN(n)) return 0;
  return unit === 'lb' ? n * LB_TO_KG : n;
};

// `expectedKg` is what this line's lot(s) should sum to — for an ordinary line that's just its own
// computed quantity, but for a recipe's base ingredient (e.g. raw mushrooms cooked down into the
// sauce), the real amount PULLED FROM INVENTORY is the raw weight entered at the top of the run
// page, not the cooked quantity shown in this row — the caller passes whichever is correct.
export default function LotUsageRow({ ingredientId, expectedKg, onChange }) {
  const [open, setOpen] = useState(false);
  const [usages, setUsages] = useState([]); // [{lotCode, quantity, unit}]
  const [availableLots, setAvailableLots] = useState(null);

  const emit = (next) => {
    setUsages(next);
    onChange(
      next
        .filter(u => u.lotCode?.trim() && u.quantity)
        .map(u => ({ lotCode: u.lotCode, quantityKg: toKg(u.quantity, u.unit) }))
    );
  };

  const loadLots = async () => {
    if (availableLots) return;
    try {
      setAvailableLots(await ingredientInventoryService.getLotCodesAvailable(ingredientId));
    } catch {
      setAvailableLots([]);
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadLots();
  };

  const addRow = () => emit([...usages, { lotCode: '', quantity: '', unit: 'kg' }]);
  const updateRow = (idx, field, value) => emit(usages.map((u, i) => (i === idx ? { ...u, [field]: value } : u)));
  const removeRow = (idx) => emit(usages.filter((_, i) => i !== idx));

  const totalEnteredKg = usages.reduce((s, u) => s + toKg(u.quantity, u.unit), 0);
  const pct = expectedKg > 0 ? (totalEnteredKg / expectedKg) * 100 : 0;
  const hasEntries = usages.some(u => u.lotCode?.trim() && u.quantity);
  const overBy = totalEnteredKg - expectedKg;
  const barVariant = pct > 105 ? 'bg-danger' : pct >= 95 ? 'bg-success' : 'bg-info';

  const lotOptions = (availableLots || []).map(l => ({ value: l.lotCode, hint: `${Number(l.remainingKg).toFixed(2)} kg remaining` }));

  return (
    <div className="mt-1">
      <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={toggle}>
        {open ? '▾' : '▸'} 📋 Record lot(s) used{usages.length > 0 ? ` (${usages.length})` : ''}
      </button>
      {open && (
        <div className="mt-1 ps-2" style={{ borderLeft: '2px solid #e9ecef' }}>
          {usages.map((u, idx) => (
            <div key={idx} className="mb-2">
              <div className="d-flex gap-1 mb-1 align-items-center">
                <AutocompleteInput
                  value={u.lotCode}
                  onChange={v => updateRow(idx, 'lotCode', v)}
                  onFocus={loadLots}
                  options={lotOptions}
                  placeholder="Lot code"
                  style={{ flex: 1, minWidth: 0 }}
                />
                <button type="button" className="btn btn-sm btn-outline-danger py-0 px-2 flex-shrink-0" onClick={() => removeRow(idx)}>×</button>
              </div>
              <div className="d-flex gap-1">
                <input type="number" step="0.01" className="form-control form-control-sm" style={{ maxWidth: 110 }}
                  placeholder="qty" value={u.quantity} onChange={e => updateRow(idx, 'quantity', e.target.value)} />
                <select className="form-select form-select-sm" style={{ maxWidth: 75 }} value={u.unit}
                  onChange={e => updateRow(idx, 'unit', e.target.value)}>
                  <option value="kg">kg</option>
                  <option value="lb">lb</option>
                </select>
              </div>
            </div>
          ))}
          <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }} onClick={addRow}>+ Add lot</button>

          {hasEntries && expectedKg > 0 && (
            <div className="mt-1" style={{ maxWidth: 240 }}>
              <div className="progress" style={{ height: 6 }}>
                <div className={`progress-bar ${barVariant}`} style={{ width: `${Math.min(pct, 100)}%` }} />
              </div>
              <div className="mt-1" style={{ fontSize: 10 }}>
                {totalEnteredKg.toFixed(2)} / {expectedKg.toFixed(2)} kg ({pct.toFixed(0)}%)
                {pct > 105 && (
                  <span className="text-danger ms-1">⚠ {overBy.toFixed(2)} kg more than the amount actually used — check your entries</span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
